<script setup lang="ts">
import { computed } from 'vue'

import IconDomains from '@/components/atoms/icons/IconDomains.vue'
import BetaStatusBadge from '@/components/beta-dashboard/atoms/BetaStatusBadge.vue'
import type { BetaDomain } from '@/types/beta-dashboard'

const props = withDefaults(defineProps<{
    domain: BetaDomain
    disabled?: boolean
    checking?: boolean
    statusError?: string
}>(), {
    disabled: false,
    checking: false,
    statusError: '',
})

const emit = defineEmits<{
    makeMain: [domainId: number]
    remove: [domainId: number]
    verify: [domainId: number]
}>()

const isMain = computed(() => props.domain.main_domain == 1)
const isAutomatic = computed(() => props.domain.automatic == 1)
const isOnline = computed(() => props.domain.status === 'online')
const canMakeMain = computed(() => (
    !isMain.value
    && !isAutomatic.value
    && props.domain.status === 'online'
))
const canRemove = computed(() => !isMain.value && !isAutomatic.value)
const statusLabel = computed(() => {
    if (props.checking) return 'Verificando conexão…'
    if (props.statusError) return 'Não foi possível verificar'
    if (props.domain.status === 'online') return 'Online'
    if (props.domain.status === 'provisioning') return 'DNS conectado · preparando HTTPS'
    if (props.domain.status === 'offline') return 'DNS não encontrado'
    return 'Aguardando DNS'
})
</script>

<template>
    <article :class="['beta-domain-card', isMain && 'is-main']">
        <div class="beta-domain-card__icon" aria-hidden="true">
            <IconDomains />
            <span :class="{ 'is-online': isOnline, 'is-error': props.statusError }" />
        </div>

        <div class="beta-domain-card__content">
            <div class="beta-domain-card__title">
                <strong>{{ props.domain.domain }}</strong>
                <BetaStatusBadge v-if="isMain" label="Principal" tone="accent" />
            </div>

            <div class="beta-domain-card__meta">
                <span>{{ isAutomatic ? 'Endereço temporário' : 'Domínio personalizado' }}</span>
                <span aria-hidden="true">·</span>
                <span
                    :class="[
                        'beta-domain-card__status',
                        isOnline && 'is-online',
                        props.statusError && 'is-error',
                    ]"
                >
                    {{ statusLabel }}
                </span>
            </div>
            <small v-if="props.statusError" class="beta-domain-card__error">
                {{ props.statusError }}
            </small>
        </div>

        <div v-if="!isAutomatic || canMakeMain || canRemove" class="beta-domain-card__actions">
            <button
                v-if="!isAutomatic"
                type="button"
                class="is-verify"
                :disabled="props.disabled || props.checking"
                @click="emit('verify', props.domain.id)"
            >
                {{ props.checking ? 'Verificando…' : (isOnline ? 'Verificar novamente' : 'Verificar conexão') }}
            </button>
            <button
                v-if="canMakeMain"
                type="button"
                class="is-primary"
                :disabled="props.disabled"
                @click="emit('makeMain', props.domain.id)"
            >
                Tornar principal
            </button>
            <button
                v-if="canRemove"
                type="button"
                class="is-danger"
                :disabled="props.disabled"
                :aria-label="`Remover o domínio ${props.domain.domain}`"
                @click="emit('remove', props.domain.id)"
            >
                Remover
            </button>
        </div>
    </article>
</template>

<style scoped>
.beta-domain-card {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) auto;
    align-items: center;
    gap: 13px;
    border: 1px solid #e4eaf2;
    border-radius: 17px;
    background: #fff;
    padding: 14px;
    transition: border-color 160ms ease;
}

.beta-domain-card:hover {
    border-color: #d3dce8;
}

.beta-domain-card.is-main {
    border-color: color-mix(in srgb, var(--main-color) 28%, white);
    background:
        radial-gradient(circle at 100% 0, color-mix(in srgb, var(--main-color) 8%, transparent), transparent 48%),
        #fff;
}

.beta-domain-card__icon {
    position: relative;
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border-radius: 13px;
    background: color-mix(in srgb, var(--main-color) 8%, white);
    color: var(--main-color);
}

.beta-domain-card__icon :deep(svg) {
    width: 19px;
    height: 19px;
}

.beta-domain-card__icon > span {
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 10px;
    height: 10px;
    border: 2px solid #fff;
    border-radius: 50%;
    background: #d6a244;
}

.beta-domain-card__icon > span.is-online {
    background: #34a26d;
}

.beta-domain-card__icon > span.is-error {
    background: #c96262;
}

.beta-domain-card__content {
    min-width: 0;
}

.beta-domain-card__title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 9px;
}

.beta-domain-card__title strong {
    overflow: hidden;
    color: #1a304e;
    font-size: 12px;
    font-weight: 850;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-domain-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;
    color: #8795a9;
    font-size: 10px;
    font-weight: 700;
}

.beta-domain-card__status {
    color: #9a6e2b;
}

.beta-domain-card__status.is-online {
    color: #2c8a5c;
}

.beta-domain-card__status.is-error,
.beta-domain-card__error {
    color: #ad5555;
}

.beta-domain-card__error {
    display: block;
    margin-top: 5px;
    font-size: 10px;
}

.beta-domain-card__actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.beta-domain-card__actions button {
    border-radius: var(--beta-button-radius);
    padding: 8px 10px;
    color: var(--main-color);
    font-size: 9px;
    font-weight: 850;
}

.beta-domain-card__actions button.is-primary {
    border: 1px solid color-mix(in srgb, var(--main-color) 20%, #e2e8f0);
}

.beta-domain-card__actions button.is-verify {
    border: 1px solid #dce4ee;
    color: #5c708a;
}

.beta-domain-card__actions button:hover:not(:disabled),
.beta-domain-card__actions button:focus-visible {
    background: color-mix(in srgb, var(--main-color) 7%, white);
}

.beta-domain-card__actions button.is-danger {
    padding-inline: 5px;
    color: #b45454;
}

.beta-domain-card__actions button.is-danger:hover:not(:disabled),
.beta-domain-card__actions button.is-danger:focus-visible {
    background: #fff2f2;
}

.beta-domain-card__actions button:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--main-color) 20%, transparent);
    outline-offset: 2px;
}

.beta-domain-card__actions button:disabled {
    cursor: wait;
    opacity: 0.5;
}

@media (max-width: 640px) {
    .beta-domain-card {
        grid-template-columns: 42px minmax(0, 1fr);
    }

    .beta-domain-card__actions {
        grid-column: 2;
        align-items: flex-start;
        flex-wrap: wrap;
    }
}
</style>
